import { useEffect, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

function Card({ label, value, testid, accent }) {
  return (
    <div className={`border p-6 ${accent ? "border-[#002fa7] bg-blue-50" : "border-gray-200"}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">{label}</div>
      <div className="text-3xl font-heading font-semibold tracking-tight mt-2">{value}</div>
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/config/public").then((r) => setCfg(r.data));
  };
  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    if (!window.confirm("Push all un-synced customers and invoices to Zoho Books?")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/zoho/sync-all");
      const msg = `Synced ${data.customers_synced} customers · ${data.invoices_synced} invoices` +
        (data.customers_failed + data.invoices_failed > 0
          ? ` · ${data.customers_failed + data.invoices_failed} failed`
          : "");
      (data.customers_failed + data.invoices_failed > 0) ? toast.warning(msg) : toast.success(msg);
      if (data.errors?.length) console.warn("Zoho sync errors:", data.errors);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSyncing(false); }
  };

  const setTrial = async (trial) => {
    if (!window.confirm(trial ? "Switch to TRIAL mode? No data will be pushed to Zoho Books until you switch back." : "Switch to LIVE mode? All new orders, customers and payments will be pushed to your real Zoho Books account.")) return;
    try {
      await api.post("/admin/zoho/trial-mode", { trial });
      toast.success(trial ? "Trial mode ON — Zoho writes paused" : "Live mode ON — Zoho writes resumed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!stats) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">Overview</p>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1" data-testid="admin-overview-title">Operations dashboard</h1>
        </div>
        {cfg?.zoho_mode === "live" && (
          <Button onClick={syncAll} disabled={syncing} variant="outline" className="rounded-sm border-[#002fa7] text-[#002fa7] hover:bg-blue-50" data-testid="sync-all-zoho-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5}/>
            {syncing ? "Syncing…" : "Sync all to Zoho"}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card label="Customers" value={stats.total_customers} accent testid="admin-stat-customers"/>
        <Card label="Orders" value={stats.total_orders} testid="admin-stat-orders"/>
        <Card label="Invoices" value={stats.total_invoices} testid="admin-stat-invoices"/>
        <Card label="Billed value" value={inr(stats.outstanding)} testid="admin-stat-outstanding"/>
      </div>
      {cfg && (
        <div className="mt-10 border border-gray-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <h2 className="font-heading font-semibold text-lg">Integration status</h2>
              <p className="text-xs text-gray-500 mt-1">
                {cfg.zoho_write_enabled
                  ? "LIVE — new orders push real records to your Zoho Books account."
                  : "TRIAL — no writes go to Zoho. Item imports still work."}
              </p>
            </div>
            {cfg.zoho_mode === "live" && (
              cfg.zoho_write_enabled ? (
                <Button onClick={() => setTrial(true)} variant="outline" className="rounded-sm border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="set-trial-btn">
                  Switch to Trial mode
                </Button>
              ) : (
                <Button onClick={() => setTrial(false)} className="bg-[#0e5a32] hover:bg-[#0a4626] rounded-sm" data-testid="set-live-btn">
                  Go Live with Zoho
                </Button>
              )
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between p-3 border border-gray-200">
              <span className="text-gray-600">Zoho Books</span>
              <span className={`status-pill ${cfg.zoho_write_enabled ? "status-synced" : "status-mock"}`} data-testid="cfg-zoho-mode">
                {cfg.zoho_mode === "live" ? (cfg.zoho_write_enabled ? "live writes" : "trial (read-only)") : cfg.zoho_mode}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
